import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';

// Create the theme context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useTheme = () => {
  return useContext(ThemeContext);
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Check if dark mode is stored in localStorage, default to light mode
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Create MUI theme based on dark mode
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            main: darkMode ? '#9c27b0' : '#3f51b5', // Purple for dark mode, Blue for light mode
            light: darkMode ? '#ba68c8' : '#7986cb',
            dark: darkMode ? '#7b1fa2' : '#303f9f',
            contrastText: '#ffffff'
          },
          secondary: {
            main: darkMode ? '#ff4081' : '#f50057', // Pink accent for both modes
            light: darkMode ? '#ff79b0' : '#ff4081',
            dark: darkMode ? '#c60055' : '#c51162',
            contrastText: '#ffffff'
          },
          background: {
            default: darkMode ? '#121212' : '#f5f5f5',
            paper: darkMode ? '#1e1e1e' : '#ffffff',
          },
          text: {
            primary: darkMode ? '#ffffff' : '#000000',
            secondary: darkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.6)',
          },
          action: {
            active: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.54)',
            hover: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            selected: darkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
            disabled: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            disabledBackground: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          },
          divider: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none', // Makes button text sentence case instead of all caps
              },
              contained: darkMode ? {
                backgroundColor: '#9c27b0',
                '&:hover': {
                  backgroundColor: '#ba68c8',
                },
              } : {},
              outlined: darkMode ? {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: '#ffffff',
                '&:hover': {
                  borderColor: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              } : {},
              text: darkMode ? {
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              } : {},
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: darkMode ? {
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              } : {},
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: darkMode ? {
                backgroundImage: 'none',
              } : {},
            },
          },
        },
      }),
    [darkMode]
  );

  // Update localStorage and apply theme when darkMode changes
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    
    // Apply theme to document body
    if (darkMode) {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Toggle between dark and light mode
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Value to be provided to consuming components
  const value = {
    darkMode,
    toggleDarkMode
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
